import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  ArrowLeft,
  RefreshCw,
  X,
  DollarSign,
  BarChart3,
  Download,
  Mail
} from 'lucide-react';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';

// Division groupings
const DIVISION_GROUPS = {
  'All': [], // Special case - show all divisions
  'Swim': ['10', '45', '60', '61', '77', '78'],
  'Accessories': ['15'],
  'Innovation': ['16'],
  'IMG Apparel': ['42', '55'],
  'BWI': ['30', '35', '38']
};

// Divisions to exclude from all analytics
const EXCLUDED_DIVISIONS = ['20'];

/**
 * Open WIP Analytics
 * Dashboard with data visualizations for open WIP
 * Data source: production_open_wip table
 */
export default function AnalyticsDashboard({ onBackToDashboard, embedded = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Note: termsOfficeData was removed - now using termsOfficeDivisionData with 'All' group

  // Terms vs Office by Division Group
  const [termsOfficeDivisionData, setTermsOfficeDivisionData] = useState({});
  const [selectedDivisionGroup, setSelectedDivisionGroup] = useState('All'); // Default to All

  // Drill-down modal state
  const [drillDown, setDrillDown] = useState({
    isOpen: false,
    title: '',
    data: [],
    loading: false
  });

  // Email sending state
  const [emailStatus, setEmailStatus] = useState({
    sending: false,
    message: '',
    type: '' // 'success' or 'error'
  });

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchTermsOfficeDivisionData();
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Note: fetchTermsOfficeData was removed - replaced by fetchTermsOfficeDivisionData with 'All' group

  const fetchTermsOfficeDivisionData = async () => {
    try {
      const functions = getFunctions();
      const getAnalyticsData = httpsCallable(functions, 'getAnalyticsData');

      const result = await getAnalyticsData({
        reportType: 'terms_vs_office_division'
      });

      const records = result.data.data || [];

      // Group data by division groups
      const groupedData = {};

      Object.keys(DIVISION_GROUPS).forEach(groupName => {
        groupedData[groupName] = {
          matrix: {},
          terms: new Set(),
          offices: new Set(),
          totals: { byTerms: {}, byOffice: {}, grand: 0 }
        };
      });

      // Process each record
      records.forEach(row => {
        const terms = row.terms || 'Unknown';
        const office = row.office || 'Unknown';
        const division = row.division || '';
        const fobValue = parseFloat(row.total_fob) || 0;

        // Skip excluded divisions
        if (EXCLUDED_DIVISIONS.includes(division)) return;

        // Add to "All" group
        const allGroup = groupedData['All'];
        allGroup.terms.add(terms);
        allGroup.offices.add(office);
        if (!allGroup.matrix[terms]) allGroup.matrix[terms] = {};
        if (!allGroup.matrix[terms][office]) allGroup.matrix[terms][office] = { fob: 0, count: 0 };
        allGroup.matrix[terms][office].fob += fobValue;
        allGroup.matrix[terms][office].count += parseInt(row.record_count) || 0;
        allGroup.totals.byTerms[terms] = (allGroup.totals.byTerms[terms] || 0) + fobValue;
        allGroup.totals.byOffice[office] = (allGroup.totals.byOffice[office] || 0) + fobValue;
        allGroup.totals.grand += fobValue;

        // Find which specific group this division belongs to
        for (const [groupName, divisions] of Object.entries(DIVISION_GROUPS)) {
          if (groupName === 'All') continue; // Skip "All" group

          if (divisions.includes(division)) {
            const group = groupedData[groupName];

            group.terms.add(terms);
            group.offices.add(office);

            if (!group.matrix[terms]) group.matrix[terms] = {};
            if (!group.matrix[terms][office]) group.matrix[terms][office] = { fob: 0, count: 0 };

            group.matrix[terms][office].fob += fobValue;
            group.matrix[terms][office].count += parseInt(row.record_count) || 0;

            group.totals.byTerms[terms] = (group.totals.byTerms[terms] || 0) + fobValue;
            group.totals.byOffice[office] = (group.totals.byOffice[office] || 0) + fobValue;
            group.totals.grand += fobValue;

            break;
          }
        }
      });

      // Convert sets to sorted arrays
      Object.keys(groupedData).forEach(groupName => {
        groupedData[groupName].terms = Array.from(groupedData[groupName].terms).sort();
        groupedData[groupName].offices = Array.from(groupedData[groupName].offices).sort();
      });

      setTermsOfficeDivisionData(groupedData);
      console.log('✅ Terms vs Office by Division Group data loaded');
    } catch (err) {
      console.error('❌ Error fetching terms/office division data:', err);
      throw err;
    }
  };

  // Note: fetchCountryData, fetchBreakdownData, fetchDivisionGroupData removed - charts not needed

  const handleCellClick = async (terms, office) => {
    try {
      // Determine which divisions to filter by
      const divisionsToFilter = selectedDivisionGroup === 'All'
        ? []
        : DIVISION_GROUPS[selectedDivisionGroup] || [];

      const titleSuffix = selectedDivisionGroup === 'All'
        ? ''
        : ` - ${selectedDivisionGroup}`;

      setDrillDown({
        isOpen: true,
        title: `${terms} / ${office}${titleSuffix}`,
        data: [],
        loading: true
      });

      const functions = getFunctions();
      const getAnalyticsDrillDown = httpsCallable(functions, 'getAnalyticsDrillDown');

      const result = await getAnalyticsDrillDown({
        reportType: 'terms_vs_office',
        terms: terms,
        office: office,
        divisions: divisionsToFilter
      });

      setDrillDown(prev => ({
        ...prev,
        data: result.data.data || [],
        loading: false
      }));
    } catch (err) {
      console.error('❌ Drill-down error:', err);
      setDrillDown(prev => ({
        ...prev,
        loading: false,
        data: []
      }));
    }
  };

  const closeDrillDown = () => {
    setDrillDown({ isOpen: false, title: '', data: [], loading: false });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const exportDrillDownToCSV = () => {
    if (drillDown.data.length === 0) return;

    const headers = Object.keys(drillDown.data[0]);
    const csvContent = [
      headers.join(','),
      ...drillDown.data.map(row =>
        headers.map(h => {
          let val = row[h];
          if (val === null || val === undefined) return '';
          val = String(val);
          if (val.includes(',') || val.includes('"') || val.includes('\n')) {
            return `"${val.replace(/"/g, '""').replace(/\n/g, ' ')}"`;
          }
          return val;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics_${drillDown.title.replace(/\s+/g, '_')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const emailDrillDown = async () => {
    if (drillDown.data.length === 0) return;

    setEmailStatus({ sending: true, message: '', type: '' });

    try {
      const functions = getFunctions();
      const sendAnalyticsReport = httpsCallable(functions, 'sendAnalyticsReport');

      console.log('📧 Sending analytics report via email...');
      const result = await sendAnalyticsReport({
        reportTitle: drillDown.title,
        data: drillDown.data
      });

      console.log('✅ Email sent successfully:', result.data);
      setEmailStatus({
        sending: false,
        message: `Report sent successfully! (${result.data.recordCount} records)`,
        type: 'success'
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setEmailStatus({ sending: false, message: '', type: '' });
      }, 5000);

    } catch (err) {
      console.error('❌ Error sending email:', err);
      setEmailStatus({
        sending: false,
        message: `Failed to send email: ${err.message}`,
        type: 'error'
      });

      // Clear error message after 8 seconds
      setTimeout(() => {
        setEmailStatus({ sending: false, message: '', type: '' });
      }, 8000);
    }
  };

  return (
    <div className={embedded ? "bg-gray-50 p-4" : "p-6 bg-gray-50 min-h-screen"}>
      {/* Header */}
      <div className="mb-6">
        {!embedded && (
          <button
            onClick={onBackToDashboard}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        )}
        <div className="flex items-center justify-between">
          {!embedded && (
            <div className="flex items-center gap-3">
              <BarChart3 size={28} className="text-indigo-600" />
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Open WIP Analytics</h1>
                <p className="text-sm text-gray-600">
                  Open WIP Analysis (n300_cost x pod_qty)
                </p>
              </div>
            </div>
          )}
          <button
            onClick={fetchAllData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center p-8">
          <LoadingSpinner />
        </div>
      )}

      {/* Terms vs Office Matrix with Division Group Toggle */}
      {!loading && Object.keys(termsOfficeDivisionData).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign size={18} className="text-green-600" />
                <h2 className="font-semibold text-gray-900">Terms vs Office (FOB Value)</h2>
                <span className="text-sm text-gray-500 ml-2">
                  Click any cell to see details
                </span>
              </div>
              <div className="flex items-center gap-4">
                {/* Toggle between Division Groups */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  {Object.keys(DIVISION_GROUPS).map(groupName => {
                    const groupColors = {
                      'All': 'bg-gray-700',
                      'Swim': 'bg-blue-600',
                      'Accessories': 'bg-green-600',
                      'Innovation': 'bg-purple-600',
                      'IMG Apparel': 'bg-amber-600',
                      'BWI': 'bg-rose-600'
                    };
                    const colorClass = groupColors[groupName] || 'bg-gray-600';

                    return (
                      <button
                        key={groupName}
                        onClick={() => setSelectedDivisionGroup(groupName)}
                        className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                          selectedDivisionGroup === groupName
                            ? `${colorClass} text-white shadow-sm`
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {groupName}
                      </button>
                    );
                  })}
                </div>
                {selectedDivisionGroup !== 'All' && (
                  <div className="text-sm text-gray-500">
                    Divisions: {DIVISION_GROUPS[selectedDivisionGroup]?.join(', ')}
                  </div>
                )}
                {termsOfficeDivisionData[selectedDivisionGroup] && (
                  <div className="text-sm font-medium text-gray-600">
                    Total: {formatCurrency(termsOfficeDivisionData[selectedDivisionGroup].totals.grand)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Terms vs Office Table */}
          {termsOfficeDivisionData[selectedDivisionGroup] && (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border-b border-r border-gray-200 sticky left-0 bg-gray-50 z-10">
                      Terms / Office
                    </th>
                    {termsOfficeDivisionData[selectedDivisionGroup].offices.map(office => (
                      <th key={office} className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 min-w-[120px]">
                        {office}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right text-xs font-semibold text-indigo-600 uppercase border-b border-l-2 border-gray-200 bg-indigo-50 min-w-[120px]">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {termsOfficeDivisionData[selectedDivisionGroup].terms.map((terms, idx) => (
                    <tr key={terms} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-200 sticky left-0 bg-inherit z-10">
                        {terms}
                      </td>
                      {termsOfficeDivisionData[selectedDivisionGroup].offices.map(office => {
                        const cell = termsOfficeDivisionData[selectedDivisionGroup].matrix[terms]?.[office];
                        const value = cell?.fob || 0;
                        return (
                          <td
                            key={office}
                            onClick={() => value > 0 && handleCellClick(terms, office)}
                            className={`px-4 py-3 text-sm text-right border-gray-100 ${
                              value > 0
                                ? 'text-gray-900 cursor-pointer hover:bg-indigo-50 hover:text-indigo-700 transition-colors'
                                : 'text-gray-300'
                            }`}
                          >
                            {formatCurrency(value)}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-sm text-right font-semibold text-indigo-700 border-l-2 border-gray-200 bg-indigo-50">
                        {formatCurrency(termsOfficeDivisionData[selectedDivisionGroup].totals.byTerms[terms] || 0)}
                      </td>
                    </tr>
                  ))}
                  {/* Totals Row */}
                  <tr className="bg-indigo-50 border-t-2 border-gray-200">
                    <td className="px-4 py-3 text-sm font-semibold text-indigo-700 border-r border-gray-200 sticky left-0 bg-indigo-50 z-10">
                      Total
                    </td>
                    {termsOfficeDivisionData[selectedDivisionGroup].offices.map(office => (
                      <td key={office} className="px-4 py-3 text-sm text-right font-semibold text-indigo-700">
                        {formatCurrency(termsOfficeDivisionData[selectedDivisionGroup].totals.byOffice[office] || 0)}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-sm text-right font-bold text-indigo-900 border-l-2 border-gray-200 bg-indigo-100">
                      {formatCurrency(termsOfficeDivisionData[selectedDivisionGroup].totals.grand)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Drill-Down Modal */}
      {drillDown.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Detail View</h3>
                <p className="text-sm text-gray-500">{drillDown.title}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={emailDrillDown}
                  disabled={drillDown.data.length === 0 || emailStatus.sending}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Email report to your account email"
                >
                  {emailStatus.sending ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      Email
                    </>
                  )}
                </button>
                <button
                  onClick={exportDrillDownToCSV}
                  disabled={drillDown.data.length === 0}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm disabled:opacity-50"
                >
                  <Download size={16} />
                  Export
                </button>
                <button
                  onClick={closeDrillDown}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
            </div>

            {/* Email Status Message */}
            {emailStatus.message && (
              <div className={`mx-6 mt-3 px-4 py-2 rounded-md text-sm ${
                emailStatus.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {emailStatus.message}
              </div>
            )}

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-4">
              {drillDown.loading ? (
                <div className="flex justify-center p-8">
                  <LoadingSpinner />
                </div>
              ) : drillDown.data.length === 0 ? (
                <div className="text-center text-gray-500 p-8">No data found</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {Object.keys(drillDown.data[0]).map(col => (
                        <th
                          key={col}
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap"
                        >
                          {col.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {drillDown.data.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        {Object.keys(row).map(col => (
                          <td key={col} className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {col.includes('fob') || col.includes('cost') || col.includes('value')
                              ? formatCurrency(row[col])
                              : row[col] ?? '-'
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-500">
              {drillDown.data.length} records
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
